import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { DeckPlan } from "../../services/ai/deckPlannerService";
import { DeckCreationProgressView } from "./DeckCreationProgress";
import type { FactSetPresentationRef } from "../../services/factset/intentBridgeService";
import type { BrandId } from "../../services/ai/brandRegistry";
import type { AgentTimelineState } from "../../services/ai/agentStream";

// NOTE
//
// Chat presents a Review popup for explicit plan requests; Create slides
// posts `config` and `plan` back to the taskpane.
//
// This component is now a thin host whose only job is to show the
// progress UI (`DeckCreationProgressView`) while the deck is actually
// being assembled. The legacy `DeckConfigStep`, `DeckPlanReview` and
// `BaseSlideCarousel` files have been deleted along with the old
// wizard branches that used them.

export interface AgentProgressEvent {
  id: string;
  type: "thinking" | "tool" | "observation" | "correction" | "warning" | "error";
  label: string;
  detail?: string;
  detailFormat?: "plain" | "markdown";
  toolName?: string;
  status?: "running" | "ok" | "warning" | "error";
  slideNumber?: number;
  elapsedMs?: number;
  hidden?: boolean;
}

export interface CreationProgress {
  currentSlide: number;
  currentSlideId?: string;
  totalSlides: number;
  currentSlideTitle: string;
  status: "creating" | "generating" | "complete" | "error" | "cancelled";
  message: string;
  completedSlides: string[];
  recentLogs?: string[];
  targetPresentationName?: string;
  targetPresentationId?: string;
  phase?: "generating" | "finalReview" | "polishing";
  finalReview?: {
    current: number;
    total: number;
  };
  /**
   * Post-build per-slide visual polish progress. `slideStatuses` is keyed by
   * 1-based plan-order position (aligned with the per-slide UI rows), because
   * multiple slides can be polished in parallel (no single "current" slide).
   */
  polish?: {
    current: number;
    total: number;
    slideStatuses?: Record<number, "pending" | "active" | "done" | "error">;
    slideTitles?: Record<number, string>;
  };
  agentEvents?: AgentProgressEvent[];
  agentSummary?: string;
  /** Live agent hop timeline (streamed thinking + tool calls + nested finster). */
  agentTimeline?: AgentTimelineState;
  /** Enables deck-build-only rendering of streamed slides[].summary metadata. */
  streamApplyEditSlideSummaries?: boolean;
}

export interface StyleConfig {
  source: "none" | "existing" | "pdf";
  pdfStyleImage?: string;
  description?: string;
}

export interface ExistingSlidePreview {
  index: number;
  thumbnail: string;
  shapeCount: number;
}

// Insertion targeting for `createDeck`. Relocated here (from the retired
// `allocateSlides` pipeline module) so it lives next to the other
// `onCreateDeck` signature types its callers share.
export interface AllocationOptions {
  insertAfterCurrent?: boolean;
  targetPresentationRef?: FactSetPresentationRef | null;
}

// `DeckConfig` used to live in the now-deleted `DeckConfigStep.tsx`.
// It moved here so the dialog service and App.tsx can keep importing
// it from a stable location next to its consumers.
export interface DeckConfig {
  topic: string;
  slideCount?: number;
  additionalContext: string;
  selectedTaskId: string;
  selectedTaskRunId: string;
  styleConfig: StyleConfig;
  includeTitleSlide: boolean;
  includeSummarySlide: boolean;
  insertAfterCurrent: boolean;
  /** Active company brand for this deck, or null when none selected. */
  brandId: BrandId | null;
}

interface DeckCreatorProps {
  onLog?: (message: string) => void;
  onCancel: () => void;
  onCreateDeck: (
    plan: DeckPlan,
    onProgress: (progress: CreationProgress) => void,
    styleConfig?: StyleConfig,
    allocationOptions?: AllocationOptions,
    brandId?: BrandId | null
  ) => Promise<void>;
  externalProgress?: CreationProgress | null;
  isCreating?: boolean;
  initialConfig?: DeckConfig;
  initialPlan?: DeckPlan;
  initialPresentationRef?: FactSetPresentationRef | null;
  onClearInitialConfig?: () => void;
}

export const DeckCreator: React.FC<DeckCreatorProps> = ({
  onLog,
  onCancel,
  onCreateDeck,
  externalProgress,
  isCreating,
  initialConfig,
  initialPlan,
  initialPresentationRef,
  onClearInitialConfig,
}) => {
  const [localProgress, setLocalProgress] = useState<CreationProgress | null>(null);
  const creationProgress = externalProgress ?? localProgress;

  const log = useCallback(
    (msg: string) => {
      console.log("[DeckCreator]", msg);
      onLog?.(msg);
    },
    [onLog]
  );

  const onCreateDeckRef = useRef(onCreateDeck);
  useEffect(() => {
    onCreateDeckRef.current = onCreateDeck;
  }, [onCreateDeck]);

  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  // Tracks the last `(initialConfig, initialPlan)` pair we already
  // kicked creation off for, so a re-render with the same inputs (or
  // a render that arrives after we asked the parent to clear them
  // but before it has) does NOT start a second creation job.
  const lastHandledInitialRef = useRef<{
    config: DeckConfig;
    plan: DeckPlan;
  } | null>(null);

  useEffect(() => {
    if (!initialConfig || !initialPlan) return;
    if (
      lastHandledInitialRef.current?.config === initialConfig &&
      lastHandledInitialRef.current?.plan === initialPlan
    ) {
      return;
    }
    lastHandledInitialRef.current = { config: initialConfig, plan: initialPlan };

    log(`Using plan from dialog: ${initialPlan.slides.length} slides - starting creation`);

    setLocalProgress({
      currentSlide: 0,
      totalSlides: initialPlan.slides.length,
      currentSlideTitle: "Initializing...",
      status: "creating",
      message: "Setting up presentation structure",
      completedSlides: [],
      targetPresentationName:
        initialPresentationRef?.name || initialPresentationRef?.fullName || undefined,
      targetPresentationId: initialPresentationRef?.openPresentationId,
    });

    onClearInitialConfig?.();

    const allocOptions: AllocationOptions = {
      insertAfterCurrent: initialConfig.insertAfterCurrent ?? true,
      targetPresentationRef: initialPresentationRef ?? null,
    };

    onCreateDeckRef
      .current(
        initialPlan,
        (progress) => setLocalProgress(progress),
        initialConfig.styleConfig,
        allocOptions,
        initialConfig.brandId
      )
      .then(() => log("Deck created successfully!"))
      .catch((err) => {
        const errorMsg = err instanceof Error ? err.message : String(err);
        log(`Error: ${errorMsg}`);
        // Don't strand the user on an empty progress view — exit back
        // to chat so they can reopen the dialog and retry.
        onCancelRef.current();
      });
  }, [
    initialConfig,
    initialPlan,
    initialPresentationRef,
    onClearInitialConfig,
    log,
  ]);

  // Recovery: if we somehow end up rendered without any initial inputs
  // AND without active progress (e.g. `syncDeckCreationModeWithActivePresentation`
  // forced `aiMode = "create"` during a transient state), don't render
  // a blank pane — exit back to chat. The legacy in-pane config wizard
  // was deleted along with this branch's existence; we never want to
  // present an empty creator surface to the user.
  useEffect(() => {
    if (!initialConfig && !initialPlan && !isCreating && !creationProgress) {
      onCancelRef.current();
    }
  }, [initialConfig, initialPlan, isCreating, creationProgress]);

  if (!creationProgress) {
    return null;
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <DeckCreationProgressView progress={creationProgress} onCancel={onCancel} />
    </div>
  );
};

export default DeckCreator;
